import React, { useState, useEffect } from 'react';
import { FaRobot, FaBrain, FaShieldAlt, FaChartLine } from "react-icons/fa";
import '../../../css/UserAdmin/AiInsight.css';

const AiInsight = ({ 
    endpoint, 
    title = "AI Analysis", 
    subtitle = "Analyzing your data...",
    category = "default" // options: default, security, performance, analytics
}) => {
    const [insight, setInsight] = useState(null);
    const [isTyping, setIsTyping] = useState(true);
    const [error, setError] = useState(null);

    // Icon mapping based on category
    const getIcon = () => {
        switch(category) {
            case 'security':
                return FaShieldAlt;
            case 'performance':
                return FaChartLine;
            case 'analytics':
                return FaBrain;
            default:
                return FaRobot;
        }
    };

    const getStatusClass = () => {
        if (error) return 'error';
        if (isTyping) return 'thinking';
        return 'ready';
    };

    const getSubtitle = () => {
        if (error) return 'Analysis failed';
        if (isTyping) return subtitle;
        return 'Analysis complete';
    };

    useEffect(() => {
        setIsTyping(true);
        setError(null);
        setInsight(null);

        const fetchInsight = async () => {
            if (!endpoint) {
                setError("No analysis endpoint provided.");
                setIsTyping(false);
                return;
            }
            
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`);
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ 
                        message: 'Could not parse error response.' 
                    }));
                    throw new Error(errorData.message || 'Network response was not ok');
                }
                const data = await response.json();

                // Simulate AI "thinking" time for better UX
                setTimeout(() => {
                    // Intelligent data handling
                    if (data && data.insight) {
                        setInsight(data.insight);
                    } else if (data && Array.isArray(data.insights) && data.insights.length > 0) {
                        const formattedInsights = "Key findings:\n• " + data.insights.join('\n• ');
                        setInsight(formattedInsights);
                    } else {
                        setInsight("No specific patterns or unusual activities detected at this time. Your system appears to be running normally.");
                    }
                    setIsTyping(false);
                }, 1500);

            } catch (err) {
                console.error("AI Insight fetch error:", endpoint, err);
                setError("I'm having trouble analyzing the data right now. Please try again in a moment.");
                setIsTyping(false);
            }
        };

        fetchInsight();
    }, [endpoint, subtitle]);

    const IconComponent = getIcon();

    return (
        <div className={`ai-insight-container ${category}`}>
            <div className="ai-insight-header">
                <div className="ai-avatar">
                    <IconComponent />
                </div>
                <div className="ai-header-content">
                    <h3 className="ai-title">{title}</h3>
                    <p className="ai-subtitle">{getSubtitle()}</p>
                </div>
                <div className={`ai-status-indicator ${getStatusClass()}`}></div>
            </div>
            
            <div className={`ai-content ${isTyping ? 'loading' : ''}`}>
                {isTyping ? (
                    <div className="typing-indicator">
                        <span>Analyzing</span>
                        <div className="typing-dots">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                ) : (
                    <p className={`ai-message ${error ? 'error' : ''}`}>
                        {error || insight}
                    </p>
                )}
            </div>
        </div>
    );
};

export default AiInsight;